/**
 * ADR-0231 review nit — warn before a download retires a key that may
 * still be deployed. Each download issues a new install key; at most two
 * stay active, so a third retires the oldest. If the merchant has two
 * active keys already, ask first.
 */

import type { SiteId } from '@sill/types';
import type { InstallKind, SiteInstallsResponse } from '@sill/types/api';

export interface InstallsLister {
  listSiteInstalls(site_id: SiteId): Promise<SiteInstallsResponse>;
}

/** Message to show, or null when no active key would be retired. */
export async function keyRotationWarning(
  api: InstallsLister,
  site_id: SiteId,
  kind: InstallKind,
): Promise<string | null> {
  let res: SiteInstallsResponse;
  try {
    res = await api.listSiteInstalls(site_id);
  } catch {
    return null; // never block a download on the key list
  }
  if (!res.signing_available) return null;
  const install = res.installs.find((i) => i.kind === kind && i.revoked_at === null);
  const active = (install?.keys ?? []).filter((k) => k.status === 'active');
  if (active.length < 2) return null;
  const oldest = active.reduce((a, b) => (a.created_at <= b.created_at ? a : b));
  const what = kind === 'worker' ? 'Worker' : 'plugin';
  return `This download issues a new install key and retires your oldest active key (${oldest.fingerprint}). `
    + `If the ${what} that uses that key is still deployed, it keeps working, but its reports will no longer be marked as coming from your install until you replace it. Continue?`;
}

/** True when the download should proceed. */
export async function confirmKeyRotation(
  api: InstallsLister,
  site_id: SiteId,
  kind: InstallKind,
  confirmFn: (message: string) => boolean = (m) => window.confirm(m),
): Promise<boolean> {
  const message = await keyRotationWarning(api, site_id, kind);
  return message === null ? true : confirmFn(message);
}
